/**
 * Rock Ridge overlay (POSIX semantics on top of ISO9660).
 *
 * Rock Ridge is encoded in the System Use Area (SUA) of each directory record.
 * SUSP (System Use Sharing Protocol, IEEE 1281) frames the SUA into "fields"
 * with a 2-byte signature; Rock Ridge defines a specific set of signatures.
 *
 * Fields supported:
 *   SP — SUSP indicator (root only; signature "SP")
 *   ER — Extension reference (Rock Ridge protocol identifier)
 *   CE — Continuation entry: another SUSP block at (LBA, offset, length)
 *   ST — Stop SUA processing
 *   PD — Padding
 *   NM — Long POSIX name
 *   PX — POSIX file mode + uid + gid + nlinks
 *   PN — POSIX device numbers
 *   SL — Symbolic link target (composed of components)
 *   TF — POSIX timestamps (mtime/atime/ctime/...)
 *   CL — Child link (parent of relocated dir)
 *   PL — Parent link (deep dir relocation)
 *   RE — Relocated dir marker
 *
 * Surface: window.__DiskImage.applyRockRidge(iso) → Promise<Iso9660>
 *   Adds: node.posixName, node.symlink, node.mode, node.uid, node.gid,
 *         node.nlinks, node.timestamps {mtime, atime, ctime}, node.rockRidge=true
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.applyRockRidge) return;

    var SECTOR = 2048;

    function _u16le(b, off) { return b[off] | (b[off + 1] << 8); }
    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) s += String.fromCharCode(b[off + i]);
        return s;
    }

    function _decodeTimestamp(b, off, longForm) {
        if (longForm) {
            // 17-byte ASCII form: YYYYMMDDHHMMSS00 + GMT offset
            var y = parseInt(_ascii(b, off, 4), 10);
            var mo = parseInt(_ascii(b, off + 4, 2), 10);
            var d = parseInt(_ascii(b, off + 6, 2), 10);
            var hh = parseInt(_ascii(b, off + 8, 2), 10);
            var mm = parseInt(_ascii(b, off + 10, 2), 10);
            var ss = parseInt(_ascii(b, off + 12, 2), 10);
            return { y: y, mo: mo, d: d, hh: hh, mm: mm, ss: ss };
        } else {
            // 7-byte binary form: same as ISO9660 dir record date
            return { y: b[off] + 1900, mo: b[off + 1], d: b[off + 2], hh: b[off + 3], mm: b[off + 4], ss: b[off + 5], gmtOffset15min: (b[off + 6] << 24) >> 24 };
        }
    }

    /**
     * Walk SUSP fields in a buffer slice. Calls cb(sig, version, data) per field.
     * Resolves CE continuations by re-reading from the disk via reader.
     * Stops on ST.
     */
    function _walkSusp(reader, buf, baseOff, length, ceVisited, cb) {
        if (!buf || length <= 0) return Promise.resolve();
        ceVisited = ceVisited || new Set();
        var off = baseOff || 0;
        var end = Math.min(buf.length, (baseOff || 0) + length);

        function _step() {
            while (off + 4 <= end) {
                var sig = _ascii(buf, off, 2);
                var fieldLen = buf[off + 2];
                var version = buf[off + 3];
                if (fieldLen < 4 || off + fieldLen > end) {
                    return Promise.resolve();
                }
                if (sig === 'ST') return Promise.resolve();
                if (sig === 'CE') {
                    var ceLba = _u32le(buf, off + 4);
                    var ceOff = _u32le(buf, off + 12);
                    var ceLen = _u32le(buf, off + 20);
                    var ceKey = ceLba + ':' + ceOff + ':' + ceLen;
                    off += fieldLen;
                    if (!ceVisited.has(ceKey)) {
                        ceVisited.add(ceKey);
                        return reader.bytes(ceLba * SECTOR + ceOff, ceLen).then(function (cBuf) {
                            return _walkSusp(reader, cBuf, 0, cBuf.length, ceVisited, cb);
                        }).then(function () { return _step(); });
                    }
                    continue;
                }
                if (sig.length === 2) {
                    cb(sig, version, buf.subarray(off + 4, off + fieldLen));
                }
                off += fieldLen;
            }
            return Promise.resolve();
        }
        return _step();
    }

    function _applyToNode(reader, node, ceVisited) {
        if (!node._systemUse || node._systemUse.length === 0) return Promise.resolve();
        var sysUse = node._systemUse;
        // Skip leading SP if present (root only) — tolerable elsewhere
        var startOff = 0;
        if (sysUse.length >= 7 && _ascii(sysUse, 0, 2) === 'SP') startOff = sysUse[2];
        var nameParts = [];
        var symlinkParts = [];
        var rrSeen = false;
        return _walkSusp(reader, sysUse, startOff, sysUse.length - startOff, ceVisited, function (sig, ver, data) {
            switch (sig) {
                case 'NM': {
                    rrSeen = true;
                    var flags = data[0];
                    var part = '';
                    for (var i = 1; i < data.length; i++) part += String.fromCharCode(data[i]);
                    nameParts.push({ part: part, cont: !!(flags & 0x01) });
                    break;
                }
                case 'PX': {
                    rrSeen = true;
                    if (data.length >= 32) {
                        node.mode = _u32le(data, 0);
                        node.nlinks = _u32le(data, 8);
                        node.uid = _u32le(data, 16);
                        node.gid = _u32le(data, 24);
                    }
                    break;
                }
                case 'TF': {
                    rrSeen = true;
                    var flags2 = data[0];
                    var longForm = !!(flags2 & 0x80);
                    var tsLen = longForm ? 17 : 7;
                    var ts = node.timestamps || {};
                    var p2 = 1;
                    var fields = ['ctime', 'atime', 'mtime', 'attrch', 'backup', 'expiration', 'effective'];
                    for (var b2 = 0; b2 < fields.length; b2++) {
                        if (flags2 & (1 << b2)) {
                            if (p2 + tsLen > data.length) break;
                            ts[fields[b2]] = _decodeTimestamp(data, p2, longForm);
                            p2 += tsLen;
                        }
                    }
                    node.timestamps = ts;
                    break;
                }
                case 'SL': {
                    rrSeen = true;
                    if (data.length < 1) break;
                    var slFlags = data[0];
                    var p3 = 1;
                    while (p3 < data.length) {
                        if (p3 + 2 > data.length) break;
                        var compFlags = data[p3];
                        var compLen = data[p3 + 1];
                        if (p3 + 2 + compLen > data.length) break;
                        var pcomp;
                        if (compFlags & 0x02) pcomp = '.';
                        else if (compFlags & 0x04) pcomp = '..';
                        else if (compFlags & 0x08) pcomp = '/';
                        else pcomp = _ascii(data, p3 + 2, compLen);
                        symlinkParts.push({ comp: pcomp, cont: !!(compFlags & 0x01) });
                        p3 += 2 + compLen;
                    }
                    break;
                }
                case 'PN': {
                    rrSeen = true;
                    if (data.length >= 16) {
                        node.devMajor = _u32le(data, 0);
                        node.devMinor = _u32le(data, 8);
                    }
                    break;
                }
                case 'CL':
                case 'PL':
                case 'RE': {
                    rrSeen = true;
                    node.rrRelocated = sig;
                    break;
                }
                case 'ER':
                    node.rrExtensionId = _ascii(data, 1 + data[0], data[1]);
                    break;
                default:
                    break;
            }
        }).then(function () {
            if (rrSeen) node.rockRidge = true;
            // Compose long name
            if (nameParts.length) {
                var name = '';
                nameParts.forEach(function (p) { name += p.part; });
                if (name) node.posixName = name;
            }
            if (symlinkParts.length) {
                // Reassemble symlink components
                var pieces = [];
                var cur = '';
                symlinkParts.forEach(function (p) {
                    if (p.comp === '/') {
                        if (cur) { pieces.push(cur); cur = ''; }
                        pieces.push('/');
                    } else {
                        cur += p.comp;
                        if (!p.cont) {
                            pieces.push(cur);
                            cur = '';
                        }
                    }
                });
                if (cur) pieces.push(cur);
                var sl = pieces.reduce(function (acc, x) {
                    if (x === '/') return acc + '/';
                    if (acc.endsWith('/') || acc === '') return acc + x;
                    return acc + '/' + x;
                }, '');
                node.symlink = sl;
            }
        });
    }

    function applyRockRidge(iso) {
        if (!iso || !iso.root) return Promise.resolve(iso);
        var reader = iso.reader;
        var nodes = iso.iterate();
        var ceVisited = new Set();
        var p = Promise.resolve();
        var rrCount = 0;
        nodes.forEach(function (n) {
            p = p.then(function () { return _applyToNode(reader, n, ceVisited); }).then(function () {
                if (n.rockRidge) rrCount++;
            });
        });
        return p.then(function () {
            iso.rockRidgeApplied = true;
            iso.rockRidgeCount = rrCount;
            return iso;
        }).catch(function () { return iso; });
    }

    w.__DiskImage.applyRockRidge = applyRockRidge;
})(window);
